import { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/utils/api';
import { Euro, CheckCircle, Clock, XCircle, CreditCard, TrendingUp, AlertCircle } from 'lucide-react';

const STATUS_LABELS = {
  initiated: 'Iniciado',
  pending: 'Pendente',
  paid: 'Pago',
  complete: 'Completo',
  cancelled: 'Cancelado',
  failed: 'Falhado',
};

const STATUS_ICONS = {
  paid: CheckCircle,
  complete: CheckCircle,
  pending: Clock,
  initiated: Clock,
  cancelled: XCircle,
  failed: AlertCircle,
};

const STATUS_COLORS = {
  paid: 'text-green-600 bg-green-50',
  complete: 'text-green-600 bg-green-50',
  pending: 'text-yellow-600 bg-yellow-50',
  initiated: 'text-blue-600 bg-blue-50',
  cancelled: 'text-gray-500 bg-gray-100',
  failed: 'text-red-600 bg-red-50',
};

export default function AdminPayments() {
  const { t } = useLanguage();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, paid: 0, pending: 0, revenue: 0 });

  useEffect(() => {
    api.get('/payments').then(r => {
      const data = r.data || [];
      setPayments(data);
      const paid = data.filter(p => p.payment_status === 'paid');
      setStats({
        total: data.length,
        paid: paid.length,
        pending: data.filter(p => ['pending', 'initiated'].includes(p.payment_status)).length,
        revenue: paid.reduce((sum, p) => sum + (p.amount || 0), 0),
      });
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="admin-payments">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('backoffice.payments')}</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase">Total</span>
            <CreditCard size={16} className="text-gray-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase">Pagos</span>
            <CheckCircle size={16} className="text-green-500" />
          </div>
          <p className="text-2xl font-bold text-green-600">{stats.paid}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase">Pendentes</span>
            <Clock size={16} className="text-yellow-500" />
          </div>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase">Receita Total</span>
            <TrendingUp size={16} className="text-[#CC0000]" />
          </div>
          <p className="text-2xl font-bold text-[#CC0000]">€{stats.revenue.toFixed(2)}</p>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : payments.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <CreditCard size={32} className="mx-auto mb-3 text-gray-300" />
            <p>Nenhum pagamento registado.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Serviço</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Cliente</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Valor</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Estado</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Data</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden xl:table-cell">Referência</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {payments.map(p => {
                  const StatusIcon = STATUS_ICONS[p.payment_status] || Clock;
                  return (
                    <tr key={p.id} className="hover:bg-gray-50 transition-colors" data-testid={`payment-${p.id}`}>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{p.service_name}</p>
                        <p className="text-xs text-gray-400">{p.service_type}</p>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <p className="text-sm text-gray-700">{p.user_name}</p>
                        <p className="text-xs text-gray-400">{p.user_email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-bold text-gray-900">
                          €{p.amount?.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[p.payment_status]}`}>
                          <StatusIcon size={12} />
                          {STATUS_LABELS[p.payment_status] || p.payment_status}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-xs text-gray-500">
                        {new Date(p.created_at).toLocaleString('pt', { 
                          day: '2-digit', 
                          month: 'short', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="px-4 py-3 hidden xl:table-cell">
                        <code className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                          {p.session_id?.slice(0, 16)}...
                        </code>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
